import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { renderToStaticMarkup } from "react-dom/server";
import { useSearchParams } from "react-router-dom";
import { Spinner } from "reactstrap";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import { ErrorToaster } from "components/common/toast";
import { isFoodCard } from "./cardKind";
import handleError from "utils/handleError";
import { GetMenuCardsAPI, GetStructureAPI } from "network/menuCards";
import { GetPrintSettingsAPI, SavePrintSettingsAPI } from "network/cardStyle";

// Stampa: crea la versione stampabile di una carta o di un menu (copertina + pagine interne di solo testo).
const FONTS = ["Playfair Display", "Cormorant Garamond", "Lora", "Libre Baskerville", "Merriweather", "Montserrat", "DM Sans", "Outfit"];
const MM = 3.7795; // px per mm (96 dpi)
// Formati carta in millimetri (larghezza x altezza). "custom" usa le misure scelte dall'utente.
const PAPER = { A4: [210, 297], A5: [148, 210], A3: [297, 420], Letter: [216, 279], DL: [99, 210], Menu: [230, 330] };
const PAPER_LABELS = { A4: "A4", A5: "A5", A3: "A3", Letter: "Letter", DL: "DL 10×21", Menu: "23×33 cm" };
const clampMm = (v, d) => { const n = parseInt(v, 10); return Number.isFinite(n) ? Math.min(600, Math.max(80, n)) : d; };
// Misure della pagina in px e proporzioni rispetto all'A4 (testi e margini crescono o calano con il foglio).
const geom = (size, cw, ch) => {
  const [mw, mh] = size === "custom" ? [clampMm(cw, 230), clampMm(ch, 330)] : PAPER[size] || PAPER.A4;
  const w = Math.round(mw * MM);
  const h = Math.round(mh * MM);
  const f = Math.min(1.3, Math.max(0.89, w / 794));
  return { w, h, mw, mh, f, pad: Math.round(Math.min(w, h) * 0.072), font: 14 * f, k: Math.min(w / 794, h / 1123) };
};
const GOLD = "#D9B860";
const INK = "#2B2420";

// Sfondi della copertina: id (il nome si traduce in render), css, colore del testo di partenza e cornice opzionale.
const BACKGROUNDS = [
  { id: "avorio", css: "#F5F0E1", ink: INK },
  { id: "goffrata", css: "radial-gradient(#e2d6bb 1px,transparent 1.5px) 0 0/10px 10px,#EFE6D2", ink: INK },
  { id: "cornice", css: "#FFFDF7", ink: INK, frame: "classic" },
  { id: "oro", css: "#B8920F", ink: INK, frame: "gold" },
  { id: "nero", css: "#14110E", ink: GOLD },
  { id: "bordeaux", css: "#5E0F22", ink: GOLD },
  { id: "salvia", css: "#A8B59B", ink: INK },
  { id: "notte", css: "#1B2A41", ink: GOLD },
  { id: "righe", css: "repeating-linear-gradient(#F6F0DF 0 17px,#E5DCC3 17px 18px)", ink: INK },
  { id: "rombi", css: "linear-gradient(45deg,#f1f1f1 25%,transparent 25%,transparent 75%,#f1f1f1 75%) 0 0/24px 24px,linear-gradient(45deg,#f1f1f1 25%,#fff 25%,#fff 75%,#f1f1f1 75%) 12px 12px/24px 24px", ink: INK },
  { id: "vigna", css: "linear-gradient(180deg,#6b0f2a,#1d0409)", ink: GOLD },
  { id: "tramonto", css: "linear-gradient(180deg,#f0a56a,#8a3a22)", ink: "#FFFFFF" },
  { id: "terracotta", css: "#C0532B", ink: "#FFFFFF" },
  { id: "minimal", css: "#FFFFFF", ink: INK, frame: "bar" },
  { id: "custom", css: "#25483F", ink: GOLD, custom: true },
  { id: "image", css: "#d0c8b6", ink: "#FFFFFF", image: true },
];

const euro = (n) => (n == null || n === "" ? "" : `€ ${Number(n).toFixed(2).replace(".", ",")}`);
const vint = (p) => {
  const y = p.vintage ? parseInt(p.vintage, 10) : null;
  return y && !String(p.name).includes(String(y)) ? ` ${y}` : "";
};
const initials = (s) => (s || "").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0]).join("").toUpperCase() || "R";
const readFile = (file, cb) => {
  if (!file) return;
  const r = new FileReader();
  r.onload = () => cb(r.result);
  r.readAsDataURL(file);
};

// ---- Impaginazione: i blocchi si dispongono in colonne e pagine; un vino non si spezza e un titolo non resta solo in fondo.
const flatten = (cats, opt) => {
  const items = [];
  const visible = (p) => p.is_available !== false && !p.is_hidden;
  const wines = (list, depth) => (list || []).filter(visible).forEach((p) => items.push({ t: "wine", p, depth }));
  const hasWines = (c) => (c.products || []).some(visible) || (c.children || []).some((s) => s.is_active !== false && hasWines(s));
  const walk = (c, depth) => {
    if (c.is_active === false || !hasWines(c)) return;
    items.push({ t: depth === 0 ? "cat" : "sub", name: c.name });
    wines(c.products, depth);
    (c.children || []).forEach((s) => walk(s, depth + 1));
  };
  cats.forEach((c) => walk(c, 0));
  return items;
};
const itemHeight = (it, opt, colWidth, f) => {
  if (it.t === "cat") return 50 * f;
  if (it.t === "sub") return 30 * f;
  let h = 24;
  if (it.p.grapes) h += 15;
  if (opt.detail && (it.p.winery || it.p.region)) h += 15;
  if (opt.description && it.p.description) h += Math.ceil(it.p.description.length / (colWidth / (6 * f))) * 15;
  return h * f;
};
// Spazio utile di una colonna: pagina meno margini e intestazione (righe ripetute uguali su ogni pagina).
const headerHeight = (S) => 10 * S.f * 1.3 + 17;
const columnWidth = (S, columns) => (S.w - 2 * S.pad - (columns - 1) * 30) / columns;
// heights: altezze misurate sulla pagina vera (null finché non sono pronte: si usano le stime).
const paginate = (items, S, columns, opt, heights) => {
  const colH = S.h - 2 * S.pad - headerHeight(S) - 4;
  const colW = columnWidth(S, columns);
  const hOf = (j) => (heights && heights[j] != null ? heights[j] : itemHeight(items[j], opt, colW, S.f));
  const pages = [[[]]];
  let used = 0;
  const next = () => {
    const page = pages[pages.length - 1];
    if (page.length < columns) page.push([]); else pages.push([[]]);
    used = 0;
  };
  items.forEach((it, i) => {
    const h = hOf(i);
    if (it.t !== "wine") {
      let need = 0;
      for (let j = i; j < items.length; j += 1) {
        need += hOf(j);
        if (items[j].t === "wine") break;
      }
      if (used > 0 && used + need > colH) next();
    } else if (used > 0 && used + h > colH) next();
    const page = pages[pages.length - 1];
    page[page.length - 1].push(it);
    used += h;
  });
  return pages;
};

const Item = ({ it, o }) => {
  if (it.t === "cat") return <div style={{ textAlign: "center", fontSize: "1.7em", margin: "14px 0 10px", fontFamily: `'${o.titleFont}',serif`, color: INK, lineHeight: 1.1 }}>{it.name}</div>;
  if (it.t === "sub") return <div style={{ fontSize: ".86em", letterSpacing: ".1em", textTransform: "uppercase", color: o.accent, margin: "10px 0 5px", fontWeight: 600 }}>{it.name}</div>;
  const { p } = it;
  const detail = [p.winery, p.region].filter(Boolean).join(" · ");
  return (
    <div style={{ marginBottom: 6, breakInside: "avoid" }}>
      <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
        <span style={{ fontWeight: 600 }}>{p.name}{vint(p)}</span>
        <span style={{ flex: 1, borderBottom: "1px dotted #aaa", transform: "translateY(-3px)" }} />
        <span style={{ fontWeight: 600, whiteSpace: "nowrap" }}>{euro(p.price)}</span>
      </div>
      {p.grapes && <div style={{ fontSize: ".8em", color: "#444" }}>{p.grapes}</div>}
      {o.detail && detail && <div style={{ fontSize: ".78em", color: "#777", fontStyle: "italic" }}>{detail}</div>}
      {o.description && p.description && <div style={{ fontSize: ".78em", color: "#555" }}>{p.description}</div>}
    </div>
  );
};

const Cover = ({ o, bg, S }) => {
  const k = S.k;
  const justify = { top: "flex-start", center: "center", bottom: "flex-end" }[o.vpos];
  const frame = bg.frame;
  const background = bg.custom ? o.customColor : bg.image && o.bgImage ? `url(${o.bgImage}) center/cover` : bg.css;
  return (
    <div style={{ width: S.w, height: S.h, background, position: "relative", boxSizing: "border-box", overflow: "hidden", fontFamily: `'${o.titleFont}',serif`, lineHeight: 1.5 }}>
      {frame === "classic" && <><div style={{ position: "absolute", inset: 36 * k, border: "1.5px solid #8a6a1c" }} /><div style={{ position: "absolute", inset: 46 * k, border: "1px solid #8a6a1c" }} /></>}
      {frame === "gold" && <div style={{ position: "absolute", inset: 56 * k, background: "#FFFDF7" }} />}
      {frame === "bar" && <div style={{ position: "absolute", left: 0, right: 0, bottom: 0, height: 14 * k, background: "#111" }} />}
      <div style={{ position: "absolute", inset: 0, padding: `${110 * k}px ${90 * k}px`, display: "flex", flexDirection: "column", justifyContent: justify, alignItems: { left: "flex-start", center: "center", right: "flex-end" }[o.align], textAlign: o.align, color: o.ink }}>
        {o.showLogo && (o.logo
          ? <img src={o.logo} alt="" style={{ maxHeight: 130 * k, maxWidth: 260 * k, marginBottom: 40 * k, objectFit: "contain" }} />
          : <div style={{ width: 130 * k, height: 130 * k, borderRadius: "50%", border: `2px solid ${o.ink}`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 46 * k, marginBottom: 40 * k }}>{initials(o.subtitle)}</div>)}
        <div style={{ fontSize: o.titleSize * k, lineHeight: 1.1, fontWeight: 700, fontStyle: o.italic ? "italic" : "normal" }}>{o.title}</div>
        {o.subtitle && <div style={{ fontFamily: "'Montserrat',sans-serif", fontSize: 20 * k, letterSpacing: ".25em", textTransform: "uppercase", margin: `${26 * k}px 0 ${12 * k}px` }}>{o.subtitle}</div>}
        {o.freeText && <div style={{ fontFamily: `'${o.bodyFont}',serif`, fontStyle: "italic", fontSize: 24 * k, whiteSpace: "pre-line" }}>{o.freeText}</div>}
      </div>
    </div>
  );
};

const Inner = ({ cols, n, total, o, S }) => {
  return (
    <div style={{ width: S.w, height: S.h, background: "#fff", color: "#222", boxSizing: "border-box", padding: S.pad, position: "relative", fontFamily: `'${o.bodyFont}',serif`, fontSize: S.font, lineHeight: 1.3 }}>
      <div style={{ display: "flex", justifyContent: "space-between", fontSize: 10 * S.f, letterSpacing: ".14em", textTransform: "uppercase", color: "#777", borderBottom: "1px solid #ccc", paddingBottom: 6, marginBottom: 10, fontFamily: "'Montserrat',sans-serif" }}>
        <span>{o.subtitle}</span><span>{o.cardName}</span>
      </div>
      <div style={{ display: "flex", gap: 30 }}>
        {cols.map((items, i) => <div key={i} style={{ flex: 1, minWidth: 0 }}>{items.map((it, k) => <div key={k} style={{ display: "flow-root" }}><Item it={it} o={o} /></div>)}</div>)}
      </div>
      <div style={{ position: "absolute", left: S.pad, right: S.pad, bottom: S.pad - 18 * S.f, display: "flex", justifyContent: "space-between", fontSize: 9 * S.f, letterSpacing: ".1em", textTransform: "uppercase", color: "#777", fontFamily: "'Montserrat',sans-serif" }}>
        <span>{o.footer}</span><span>{n}/{total}</span>
      </div>
    </div>
  );
};

// Pagina ridotta per l'anteprima.
const Thumb = ({ S, label, children }) => {
  const w = S.w > S.h ? 360 : Math.min(300, Math.round(420 * S.w / S.h));
  const k = w / S.w;
  return (
    <div style={{ flex: "0 0 auto", textAlign: "center" }}>
      <div style={{ width: w, height: S.h * k, boxShadow: "0 2px 14px rgba(0,0,0,.18)", overflow: "hidden" }}>
        <div style={{ transform: `scale(${k})`, transformOrigin: "top left", width: S.w, height: S.h }}>{children}</div>
      </div>
      <div className="small text-muted mt-2">{label}</div>
    </div>
  );
};

const Seg = ({ value, options, onChange }) => (
  <div className="pp-seg">
    {options.map(([v, t]) => <button key={v} type="button" className={v === value ? "on" : ""} onClick={() => onChange(v)}>{t}</button>)}
  </div>
);
const Label = ({ children }) => <div className="pp-label">{children}</div>;
const Section = ({ n, title, children }) => (
  <section className="pp-card"><h3>{n} · {title}</h3>{children}</section>
);

const css = () => `
.pp-wrap{max-width:1160px}
.pp-wrap h2{margin-bottom:4px}
.pp-card{background:#fff;border-radius:18px;padding:24px;margin-bottom:22px;color:#2b2420}
.pp-card h3{font-size:1.35rem;margin:0 0 16px;color:#2b2420}
.pp-label{font-size:.7rem;font-weight:700;letter-spacing:.08em;text-transform:uppercase;color:#6b5e55;margin:14px 0 6px}
.pp-chip{border:1px solid #cfc7b8;background:#fff;color:#2b2420;border-radius:999px;padding:6px 16px;margin:0 6px 6px 0;font-weight:600}
.pp-chip.on{background:#b8920f;border-color:#b8920f;color:#fff}
.pp-seg{display:inline-flex;flex-wrap:wrap;border:1px solid #d9d2c2;border-radius:8px;overflow:hidden}
.pp-seg button{border:0;background:#faf8f2;color:#2b2420;padding:8px 18px;border-right:1px solid #d9d2c2}
.pp-seg button:last-child{border-right:0}
.pp-seg button.on{background:#f3e6bf;font-weight:700}
.pp-card input.form-control,.pp-card textarea.form-control,.pp-card select.form-control{background:#faf8f2;color:#2b2420;border:1px solid #d9d2c2;border-radius:8px}
.pp-sw{width:78px;cursor:pointer;text-align:center;font-size:.75rem;color:#2b2420;background:none;border:0;padding:0}
.pp-sw i{display:block;width:78px;height:108px;border-radius:6px;border:1px solid #d9d2c2;margin-bottom:4px}
.pp-sw.on i{outline:3px solid #b8920f}
.pp-mm{width:90px;display:inline-block}
`;

const areaOf = (card) => (isFoodCard(card) ? "food" : "drinks");
const AREA_TITLES = { drinks: "Drinks Print", food: "Food Print" };
// Valori di partenza: la nota a piè di pagina predefinita segue la lingua dell'interfaccia (l'utente la puo' cambiare).
const defaultO = () => ({
  title: "", subtitle: "", freeText: "", titleFont: "Playfair Display", ink: "", titleSize: 64, align: "center", vpos: "center",
  italic: false, showLogo: true, customColor: "#25483F", accent: "#8a6a1c", bodyFont: "Lora", footer: i18n.t("cards:print.defaultFooter"),
});

export default function PrintIndex() {
  const { t } = useTranslation("cards");
  const [params] = useSearchParams();
  const type = params.get("type");
  const wanted = params.get("card");
  const [allCards, setAllCards] = useState(null);
  const [area, setArea] = useState(type === "menu" || type === "food" ? "food" : "drinks");
  const [cardId, setCardId] = useState("");
  const [data, setData] = useState(null);
  const [loaded, setLoaded] = useState(false); // impostazioni lette dal server: da qui in poi si salva
  const [bgId, setBgId] = useState("nero");
  const [o, setO] = useState(defaultO);
  const [size, setSize] = useState("A4");
  const [paperW, setPaperW] = useState(230);
  const [paperH, setPaperH] = useState(330);
  const [printing, setPrinting] = useState(false);
  const [columns, setColumns] = useState(1);
  const [descr, setDescr] = useState(true);
  const [logo, setLogo] = useState("");
  const [bgImage, setBgImage] = useState("");
  const logoRef = useRef(null);
  const bgRef = useRef(null);
  const set = (patch) => setO((prev) => ({ ...prev, ...patch }));
  const cards = allCards === null ? null : allCards.filter((c) => areaOf(c) === area);

  // Carte del ristorante; l'area (drinks / food) viene dal menu o dalla carta scelta.
  useEffect(() => {
    (async () => {
      try {
        const res = await GetMenuCardsAPI({ page: 1, perPage: 100, order: "position" });
        const list = res?.data?.menu_cards || [];
        setAllCards(list);
        const w = list.find((c) => c.uuid === wanted);
        if (w) setArea(areaOf(w));
        else if (type) setArea(type === "menu" || type === "food" ? "food" : "drinks");
      } catch (e) {
        ErrorToaster(handleError(e?.status_code, e?.message));
        setAllCards([]);
      }
    })();
  }, [type, wanted]);

  useEffect(() => {
    if (!cards) return;
    if (!cards.some((c) => c.uuid === cardId)) setCardId((cards.find((c) => c.uuid === wanted) || cards[0])?.uuid || "");
  }, [allCards, area]); // eslint-disable-line

  // Impostazioni salvate per quest'area (Drinks e Food sono separate).
  useEffect(() => {
    setLoaded(false);
    let active = true;
    (async () => {
      try {
        const res = await GetPrintSettingsAPI(area);
        const s = res?.data?.settings || {};
        if (!active) return;
        const { bgId: b, size: sz, paperW: pw, paperH: ph, columns: c, descr: d, logo: l, bgImage: bi, ...rest } = s;
        setBgId(b || "nero");
        setSize(sz || "A4");
        setPaperW(pw || 230);
        setPaperH(ph || 330);
        setColumns(c || 1);
        setDescr(d !== false);
        setLogo(l || "");
        setBgImage(bi || "");
        setO((prev) => ({ ...defaultO(), title: prev.title, titleFor: prev.titleFor, ...rest }));
      } catch (e) { /* si parte dai valori predefiniti */ }
      if (active) setLoaded(true);
    })();
    return () => { active = false; };
  }, [area]);

  useEffect(() => {
    if (!cardId) return;
    setData(null);
    (async () => {
      try {
        const res = await GetStructureAPI(cardId);
        const d = res.data;
        setData(d);
        setO((prev) => ({ ...prev, title: d.name, titleFor: cardId }));
      } catch (e) {
        ErrorToaster(handleError(e?.status_code, e?.message));
      }
    })();
  }, [cardId]);

  // Salvataggio automatico (dopo una breve pausa) delle impostazioni dell'area.
  useEffect(() => {
    if (!loaded) return undefined;
    const t = setTimeout(() => {
      const { title, titleFor, accent, ...rest } = o; // il titolo è quello della carta scelta
      SavePrintSettingsAPI(area, { ...rest, bgId, size, paperW: clampMm(paperW, 230), paperH: clampMm(paperH, 330), columns, descr, logo, bgImage }).catch(() => {});
    }, 900);
    return () => clearTimeout(t);
  }, [loaded, area, o, bgId, size, paperW, paperH, columns, descr, logo, bgImage]);

  const bg = BACKGROUNDS.find((b) => b.id === bgId) || BACKGROUNDS[0];
  const ink = o.ink || bg.ink;
  const cardName = (cards || []).find((c) => c.uuid === cardId)?.name || "";
  const opts = { ...o, ink, logo, bgImage, cardName, description: descr, detail: true };
  const G = useMemo(() => geom(size, paperW, paperH), [size, paperW, paperH]);
  const sizeLabel = size === "custom" ? `${G.mw}×${G.mh} mm` : PAPER_LABELS[size];
  const items = useMemo(() => (data ? flatten(data.categories || [], opts) : []), [data, descr]); // eslint-disable-line
  const colW = columnWidth(G, columns);
  // Misura l'altezza reale di ogni riga (con i caratteri scelti) per riempire le pagine in modo uniforme.
  const measureRef = useRef(null);
  const [heights, setHeights] = useState(null);
  const [fontsTick, setFontsTick] = useState(0);
  useEffect(() => {
    if (!document.fonts) return undefined;
    const bump = () => setFontsTick((n) => n + 1);
    document.fonts.ready.then(bump);
    document.fonts.addEventListener?.("loadingdone", bump);
    return () => document.fonts.removeEventListener?.("loadingdone", bump);
  }, []);
  useLayoutEffect(() => {
    const box = measureRef.current;
    if (!box || !items.length) { setHeights(null); return; }
    setHeights([...box.children].map((el) => el.getBoundingClientRect().height));
  }, [items, colW, G, o.bodyFont, o.titleFont, o.accent, fontsTick]);
  const pages = useMemo(
    () => (items.length ? paginate(items, G, columns, opts, heights && heights.length === items.length ? heights : null) : []),
    [items, G, columns, heights], // eslint-disable-line
  );
  const chip = (c) => <button key={c.uuid} type="button" className={`pp-chip ${c.uuid === cardId ? "on" : ""}`} onClick={() => setCardId(c.uuid)}>{c.name}</button>;
  const fontHref = `https://fonts.googleapis.com/css2?${[...FONTS, "Montserrat"].map((f) => `family=${f.replace(/ /g, "+")}:ital,wght@0,400;0,600;0,700;1,400;1,600`).join("&")}&display=swap`;
  const sheets = [
    <Cover key="c" o={opts} bg={bg} S={G} />,
    ...pages.map((cols, i) => <Inner key={i} cols={cols} n={i + 2} total={pages.length + 1} o={opts} S={G} />),
  ];

  // Stampa solo le pagine della carta, in un documento a parte: misura esatta del foglio in mm, nessuna
  // riduzione per la pagina della console e colori di sfondo sempre stampati.
  const printSheets = async () => {
    setPrinting(true);
    const frame = document.createElement("iframe");
    frame.setAttribute("aria-hidden", "true");
    frame.style.cssText = "position:fixed;right:0;bottom:0;width:0;height:0;border:0";
    document.body.appendChild(frame);
    try {
      const body = renderToStaticMarkup(<>{sheets.map((sheet, i) => <div className="sheet" key={i}>{sheet}</div>)}</>);
      const safe = (cardName || "Carta").replace(/[<>&"]/g, "");
      const doc = frame.contentDocument;
      doc.open();
      doc.write(`<!doctype html><html><head><meta charset="utf-8"><title>${safe}</title>`
        + `<link rel="stylesheet" href="${fontHref}">`
        + `<style>@page{size:${G.mw}mm ${G.mh}mm;margin:0}html,body{margin:0;padding:0;background:#fff}`
        + `*{box-sizing:border-box;-webkit-print-color-adjust:exact;print-color-adjust:exact}`
        + `.sheet{width:${G.mw}mm;height:${G.mh}mm;overflow:hidden;break-after:page;page-break-after:always}`
        + `.sheet:last-child{break-after:auto;page-break-after:auto}</style></head><body>${body}</body></html>`);
      doc.close();
      const link = doc.querySelector("link");
      await Promise.race([
        new Promise((ok) => { if (link.sheet) ok(); else { link.onload = ok; link.onerror = ok; } }),
        new Promise((ok) => setTimeout(ok, 4000)),
      ]);
      if (doc.fonts?.ready) await Promise.race([doc.fonts.ready, new Promise((ok) => setTimeout(ok, 4000))]);
      await Promise.all([...doc.images].map((img) => (img.complete ? null : new Promise((ok) => { img.onload = ok; img.onerror = ok; }))));
      const win = frame.contentWindow;
      win.addEventListener("afterprint", () => setTimeout(() => frame.remove(), 500));
      win.focus();
      win.print();
      setTimeout(() => { if (document.body.contains(frame)) frame.remove(); }, 60000);
    } catch (e) {
      frame.remove();
      ErrorToaster(t("print.printFailed"));
    }
    setPrinting(false);
  };

  return (
    <div className="content">
      <style>{css()}</style>
      <link rel="stylesheet" href={fontHref} />
      <div ref={measureRef} aria-hidden="true" style={{ position: "absolute", left: -100000, top: 0, visibility: "hidden", pointerEvents: "none", width: colW, fontFamily: `'${o.bodyFont}',serif`, fontSize: G.font, lineHeight: 1.3, color: "#222" }}>
        {items.map((it, k) => <div key={k} style={{ display: "flow-root" }}><Item it={it} o={opts} /></div>)}
      </div>
      <div className="pp-wrap">
        <h2>{AREA_TITLES[area]}</h2>
        <p className="text-muted mb-4">{t("print.intro")}</p>

        {cards === null || !loaded ? <div className="text-center py-5"><Spinner /></div> : cards.length === 0 ? (
          <div className="pp-card">{t("print.noCards", { product: area === "food" ? "Food Cards" : "Drinks Cards" })}</div>
        ) : (
          <>
            <Section n="1" title={t("print.s1")}>
              <div><span className="small text-muted mr-2">{area === "food" ? "Food Cards:" : "Drinks Cards:"}</span>{cards.map(chip)}</div>
            </Section>

            <Section n="2" title={t("print.s2")}>
              <div className="row">
                <div className="col-lg-7">
                  <Label>{t("print.background")}</Label>
                  <div className="d-flex flex-wrap" style={{ gap: 14 }}>
                    {BACKGROUNDS.map((b) => (
                      <button key={b.id} type="button" className={`pp-sw ${b.id === bgId ? "on" : ""}`} onClick={() => setBgId(b.id)}>
                        <i style={{ background: b.custom ? o.customColor : b.css, boxShadow: b.frame === "bar" ? "inset 0 -8px #111" : b.frame === "gold" ? "inset 0 0 0 10px #B8920F, inset 0 0 0 100px #fffdf7" : undefined }} />
                        {t(`print.backgrounds.${b.id}`)}
                      </button>
                    ))}
                  </div>
                  <div className="d-flex align-items-center mt-3" style={{ gap: 12 }}>
                    <input type="color" value={o.customColor} onChange={(e) => { set({ customColor: e.target.value }); setBgId("custom"); }} title={t("print.customColor")} style={{ width: 44, height: 38, border: 0, background: "none" }} />
                    <span className="small text-muted">{t("print.customColor")}</span>
                    <input ref={bgRef} type="file" accept="image/*" hidden onChange={(e) => readFile(e.target.files[0], (d) => { setBgImage(d); setBgId("image"); })} />
                    <button type="button" className="btn btn-sm btn-secondary" onClick={() => bgRef.current?.click()}>{t("print.uploadBg")}</button>
                  </div>

                  <Label>{t("print.title")}</Label>
                  <input className="form-control" value={o.title} onChange={(e) => set({ title: e.target.value })} />
                  <Label>{t("print.subtitle")}</Label>
                  <input className="form-control" value={o.subtitle} onChange={(e) => set({ subtitle: e.target.value })} placeholder={t("print.subtitlePlaceholder")} />
                  <Label>{t("print.freeText")}</Label>
                  <textarea className="form-control" rows={3} value={o.freeText} onChange={(e) => set({ freeText: e.target.value })} placeholder={t("print.freeTextPlaceholder")} />
                  <div className="row">
                    <div className="col-6"><Label>{t("print.titleFont")}</Label>
                      <select className="form-control" value={o.titleFont} onChange={(e) => set({ titleFont: e.target.value })}>{FONTS.map((f) => <option key={f}>{f}</option>)}</select></div>
                    <div className="col-6"><Label>{t("print.textColor")}</Label>
                      <input type="color" value={ink} onChange={(e) => set({ ink: e.target.value })} style={{ width: "100%", height: 38, border: "1px solid #d9d2c2", borderRadius: 8, background: "#faf8f2" }} /></div>
                  </div>
                  <Label>{t("print.titleSize")}</Label>
                  <input type="range" min="36" max="120" value={o.titleSize} onChange={(e) => set({ titleSize: Number(e.target.value) })} style={{ width: "100%", accentColor: "#b8920f" }} />
                  <div className="row">
                    <div className="col-6"><Label>{t("print.align")}</Label><Seg value={o.align} onChange={(v) => set({ align: v })} options={[["left", t("print.left")], ["center", t("print.center")], ["right", t("print.right")]]} /></div>
                    <div className="col-6"><Label>{t("print.textPosition")}</Label><Seg value={o.vpos} onChange={(v) => set({ vpos: v })} options={[["top", t("print.top")], ["center", t("print.center")], ["bottom", t("print.bottom")]]} /></div>
                  </div>
                  <label className="d-flex align-items-center mt-3" style={{ gap: 8 }}><input type="checkbox" checked={o.italic} onChange={(e) => set({ italic: e.target.checked })} /> {t("print.italic")}</label>
                  <Label>{t("print.logo")}</Label>
                  <label className="d-flex align-items-center" style={{ gap: 8 }}><input type="checkbox" checked={o.showLogo} onChange={(e) => set({ showLogo: e.target.checked })} /> {t("print.showLogo")}</label>
                  <input ref={logoRef} type="file" accept="image/*" hidden onChange={(e) => readFile(e.target.files[0], setLogo)} />
                  <button type="button" className="btn btn-sm btn-secondary mt-2" onClick={() => logoRef.current?.click()}>{t("print.uploadLogo")}</button>
                  <span className="small text-muted ml-2">{t("print.logoHint")}</span>
                </div>
                <div className="col-lg-5">
                  <div style={{ background: "#f3efe6", borderRadius: 14, padding: 20, textAlign: "center", position: "sticky", top: 20 }}>
                    <div className="small font-weight-bold text-muted mb-3" style={{ letterSpacing: ".08em" }}>{t("print.coverPreview", { size: sizeLabel })}</div>
                    <div style={{ display: "inline-block" }}><Thumb S={G} label=""><Cover o={opts} bg={bg} S={G} /></Thumb></div>
                  </div>
                </div>
              </div>
            </Section>

            <Section n="3" title={t("print.s3")}>
              <div className="d-flex flex-wrap" style={{ gap: 28 }}>
                <div><Label>{t("print.format")}</Label>
                  <Seg value={size} onChange={setSize} options={[...Object.keys(PAPER).map((k) => [k, PAPER_LABELS[k]]), ["custom", t("print.customSize")]]} />
                  {size === "custom" && (
                    <div className="d-flex align-items-center mt-2" style={{ gap: 8 }}>
                      <input className="form-control pp-mm" type="number" min="80" max="600" value={paperW} onChange={(e) => setPaperW(e.target.value)} aria-label={t("print.widthMm")} />
                      <span>×</span>
                      <input className="form-control pp-mm" type="number" min="80" max="600" value={paperH} onChange={(e) => setPaperH(e.target.value)} aria-label={t("print.heightMm")} />
                      <span className="small text-muted">mm</span>
                    </div>
                  )}
                </div>
                <div><Label>{t("print.columns")}</Label><Seg value={columns} onChange={setColumns} options={[[1, t("print.one")], [2, t("print.two")]]} /></div>
                <div><Label>{t("print.descriptions")}</Label><Seg value={descr ? "y" : "n"} onChange={(v) => setDescr(v === "y")} options={[["y", t("print.show")], ["n", t("print.hide")]]} /></div>
                <div><Label>{t("print.bodyFont")}</Label>
                  <select className="form-control" value={o.bodyFont} onChange={(e) => set({ bodyFont: e.target.value })}>{FONTS.map((f) => <option key={f}>{f}</option>)}</select></div>
                <div style={{ minWidth: 220 }}><Label>{t("print.footerNote")}</Label>
                  <input className="form-control" value={o.footer} onChange={(e) => set({ footer: e.target.value })} /></div>
              </div>
              <p className="small text-muted mt-3">{t("print.layoutHint")}</p>
              {!data ? <div className="text-center py-4"><Spinner /></div> : (
                <div style={{ display: "flex", gap: 18, overflowX: "auto", padding: "6px 4px 14px" }}>
                  <Thumb S={G} label={t("print.cover")}>{sheets[0]}</Thumb>
                  {pages.map((cols, i) => <Thumb key={i} S={G} label={t("print.page", { n: i + 2 })}>{sheets[i + 1]}</Thumb>)}
                </div>
              )}
              {data && pages.length === 0 && <p className="text-muted">{t("print.noContent")}</p>}
              <button type="button" className="btn btn-dark mt-3" disabled={!data || printing} onClick={printSheets}>{t("print.generate")}</button>
              <span className="small text-muted ml-3">{t("print.pdfHint")}</span>
            </Section>
          </>
        )}
      </div>
    </div>
  );
}
